// Stats — a player's stats for one Catalyst category (Appearance / Passing / Attacking /
// Defending / Goalkeeping, chosen by modCfg.categoryId), ported from js/renderers-stats.js's
// window.renderStatsModule via the 04b bridge. Two modes, dispatched on mod._mergedPlayers:
//   - SINGLE-PLAYER (04h-2, StatsSinglePlayer): compare-against percentile bars + population.
//   - COMPARISON (04h-3, StatsComparison): up-to-5 players merged onto one module, two-to-five
//     colour-coded markers per bar, no filter/population — a different question (named players
//     vs each other, not vs a filtered population). The drag-to-merge GESTURE is Phase 05;
//     04h-3 renders the merge RESULT (seed mod._mergedPlayers by hand to verify). On ?live=1
//     (T2-14) it reads real numbers: StatsComparisonLive.
// The dispatcher itself calls no hooks, so the two modes keep independent hook orders — a
// merge/unmerge (which flips _mergedPlayers then calls refreshModuleBody) cleanly swaps them.
//
// needsPlayer:true — main.js's createModule already built the .module-sub-controls strip with
// the shared player search/select (js/main.js:2314), and its 'change' re-renders us with the
// new playerName prop. So we DON'T own the header player picker (the sticky-player mirror gap
// stays open for Phase 05, same as every needsPlayer module); single-player adds its season
// select INTO that existing strip via <SubControlsSlot>, and comparison HIDES the whole strip
// (no per-player picker across a merge), exactly as the legacy does.

import { isLowerBetter } from "./radar-view.js";
import { ModuleExplainer } from "./ModuleExplainer.js";
import { explainStatsMerged, explainStatsTile } from "./explain.js";
import { isLeagueCleared, useLeagueChip } from "../hooks/useLeagueChip.js";
import { useEffect, useMemo, useReducer, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { type ProtoModuleProps, getModState, setModState } from "./bridge.js";
import { SubControlsSlot } from "./SubControlsSlot.js";
import { StyledSelect, type StyledSelectItem } from "./StyledSelect.js";
import { PopulationReadout } from "./PopulationReadout.js";
import { FilterChipBar } from "./FilterChipBar.js";
import { humanizeSeasonCode } from "./league-table-view.js";
import {
  computeFilteredPopulation,
  fullRange,
  filteredExtent,
  markerFraction,
  percentBetter,
  percentBetterText,
  rangeBarFraction,
  categoryHasTotalMode,
  shouldSkipGroupLabel,
  type Range,
} from "./stats-view.js";
import { useMetricCategories } from "../hooks/useMetricCategories.js";
import { useLiveStatsComparison, useStatsGroup, useStatsTile } from "../hooks/usePlayerStats.js";
import { LIVE } from "../live/flag.js";
import { liveStatsCategory } from "../live/stats.js";
import {
  LIVE_RANGE_FILTERS,
  MINUTES_CHIP_DEFAULT,
  seasonChipFromValue,
  seasonChipValue,
  seasonLabel as fileSeasonLabel,
  type SeasonChip,
} from "../live/stats-files.js";
import { pickedPlayer, pickedShortName } from "../live/picked-players.js";
import { useFilterOptions } from "../hooks/useFilterOptions.js";
import { readSnapshot, snapshotPlayersOrNone } from "../data/snapshot.js";
import { getPlayerShortName, getPlayerInitialName } from "../data/player-utils.js";
import { playerById, playerIdOf, playerIdOfName, type PlayerId } from "../data/players.js";
import type { ProtoMetricCategory, ProtoPlayerMetric } from "@catbento/shared";
import type { RawPlayerRecord } from "../data/snapshot.js";
import {
  hasTotalMode,
  metricValueForMode,
  formatValueForMode,
  formatMetricValue,
  isRangeFilter,
  formatRangeReadout,
  FILTER_LABELS,
  RANGE_FILTERS,
  type ActiveFilters,
  type ExcludeValue,
  type KitPlayer,
  type RangeActive,
  selectedValuesOf,
  type CategoricalActive,
} from "../engines/stats-filter-kit.js";
import { setStickyPlayer } from "../canvas/sticky-player.js";
import { startMenuDrag, startPlayerTokenDrag } from "../canvas/menu-drag.js";
import { unmergePlayer } from "../canvas/drop-actions.js";
import { teamOfModule, type TeamRef } from "../live/team.js";
import { TeamStats } from "./TeamStats.js";
import { TeamStatsComparison } from "./TeamStatsComparison.js";
import { mergedTeamsOf } from "../canvas/team-merge.js";
import { loadStickyClub } from "../live/squad.js";
import { useLiveQuery } from "../live/query.js";
import { liveApi } from "../live/api.js";
import { getStickyPlayer } from "../canvas/sticky-player.js";

// Live chip bar (T2-12b): Season shows quieter while untouched. The add menu, the chips' values and
// her details come with the group (live/stats-files.ts ChipBarSetup).
const LIVE_CHIP_LABELS = { season: "Season" };
const QUIET_SEASON: ReadonlySet<string> = new Set(["season"]);

// The scale a metric reads on under the active mode — "total" only when it has one.
function scaleFor(metric: ProtoPlayerMetric, mode: string): "per90" | "total" {
  return mode === "total" && hasTotalMode(metric) ? "total" : "per90";
}

// Per-player colour/letter identity in comparison mode (CSS: .marker-a../.compare-chip-a..).
const COMPARE_LETTERS = ["a", "b", "c", "d", "e"];

// Dispatcher: comparison when the module carries a merged-player set, else single-player. No
// hooks here, so each mode owns its own hook order across a merge/unmerge refresh.
/** Both quiet chips (an untouched Season and League) as one set for the chip bar. */
function mergeQuiet(a: ReadonlySet<string> | undefined, b: ReadonlySet<string> | undefined): ReadonlySet<string> | undefined {
  if (!a) return b;
  if (!b) return a;
  return new Set([...a, ...b]);
}

export function StatsModule(props: ProtoModuleProps) {
  // A team shown here (teams and matches, Ben 2026-10-30): its numbers among its league's teams.
  const team = LIVE ? teamOfModule(props.mod) : null;
  if (team) {
    // Teams compared (canvas/team-merge.ts), else the one team.
    const teams = mergedTeamsOf(props.mod);
    const categoryId = (props.modCfg as { categoryId?: string } | null)?.categoryId;
    if (teams && categoryId) return <TeamStatsComparison mod={props.mod} body={props.body} teams={teams} categoryId={categoryId} />;
    return <StatsTeam {...props} team={team} />;
  }
  // A team-only tile (Possession, Set Pieces, Discipline) with no team yet: the current player's club.
  const categoryId = (props.modCfg as { categoryId?: string } | null)?.categoryId;
  if (LIVE && categoryId && TEAM_ONLY_CATEGORIES.has(categoryId)) return <StatsStickyClub {...props} />;
  const merged = getModState<PlayerId[]>(props.mod, "_mergedPlayers");
  if (!merged || !merged.length) return <StatsSinglePlayer {...props} />;
  return LIVE ? <StatsComparisonLive {...props} /> : <StatsComparison {...props} />;
}

/** The tiles that only show a team: the engine's team-only groups (Ben, 2026-10-30). */
const TEAM_ONLY_CATEGORIES: ReadonlySet<string> = new Set(["possession", "set_pieces", "discipline"]);

/** A team-only tile opens on the current player's club, as a League Table opens on their league;
 *  kept on the module once found (a later player change doesn't swap it). */
function StatsStickyClub(props: ProtoModuleProps) {
  const { mod } = props;
  const sticky = getStickyPlayer();
  const club = useLiveQuery(sticky === null ? null : `squad:sticky:${sticky}`, () => loadStickyClub(liveApi(), sticky as PlayerId));
  const team: TeamRef | null =
    club && club.teamId !== null && club.competitionId !== null && club.teamName !== null
      ? { id: club.teamId, name: club.teamName, competitionId: club.competitionId, season: null }
      : null;
  useEffect(() => {
    if (team) setModState(mod, "_team", team);
  }, [mod, team]);
  if (!team) return <div className="stats-empty">Drop a team here: a League Table row, a team in Match Results, or Squad&apos;s club.</div>;
  return <StatsTeam {...props} team={team} />;
}

function StatsTeam({ mod, body, modCfg, team }: ProtoModuleProps & { team: TeamRef }) {
  const [, forceRender] = useReducer((x: number) => x + 1, 0);
  const categoryId = (modCfg as { categoryId?: string } | null)?.categoryId;
  if (!categoryId) throw new Error("A Stats tile without a category");
  return <TeamStats mod={mod} body={body} team={team} categoryId={categoryId} onChange={forceRender} />;
}

/** The tile's category: the snapshot's; on ?live=1 with Claim Success % swapped for Ben's two
 *  new keeper rows (Q-015 B, live/stats.ts `liveStatsCategory`). Memoised: the tile's data keys
 *  hang off it. */
function useStatsCategory(categoryId: string | null): ProtoMetricCategory | null {
  const categories = useMetricCategories();
  return useMemo(() => {
    const category = categoryId ? (categories.find((c) => c.id === categoryId) ?? null) : null;
    return category && LIVE ? liveStatsCategory(category) : category;
  }, [categories, categoryId]);
}

function StatsSinglePlayer({ mod, body, playerId, playerName, modCfg }: ProtoModuleProps) {
  const categoryId = (modCfg as { categoryId?: string } | null)?.categoryId ?? null;
  const category = useStatsCategory(categoryId);

  const [, forceRender] = useReducer((x: number) => x + 1, 0);

  // Resolve the shown player: the module's own .player-select value (playerName), falling
  // back to the first snapshot player when it's absent (renderers-stats.js:784). Live (T2-4):
  // exactly the module's player, any player in the DB — never the first-player fall-back.
  const players = snapshotPlayersOrNone();
  const name = LIVE ? (playerName ?? "") : playerName && players[playerName] ? playerName : (Object.keys(players)[0] ?? "");
  // Who that is, by id — what this module stores (ids everywhere).
  const shownId = LIVE ? playerId : name ? playerIdOfName(players, name) : null;

  // Which season this instance shows. Persisted as _selectedSeason, tagged with the player
  // it belongs to (_selectedSeasonPlayer) so it auto-resets to the new player's currentSeason
  // when the player changes — a chosen season is meaningless for a different player
  // (renderers-stats.js:804). usePlayerStats resolves the fallback to currentSeason.
  const seasonOwner = getModState<PlayerId>(mod, "_selectedSeasonPlayer");
  const requestedSeason = seasonOwner === shownId ? getModState<string>(mod, "_selectedSeason") : undefined;
  // Which competition (live only): _selectedCompetition, tagged with its player the same way.
  // A season pick clears it, so a new player OR a new season goes back to her league (Ben,
  // 2026-10-08). Unset / not one of hers that season → her league (useStatsTile resolves it).
  const competitionOwner = getModState<PlayerId>(mod, "_selectedCompetitionPlayer");
  const requestedCompetition =
    competitionOwner === shownId ? (getModState<string | null>(mod, "_selectedCompetition") ?? undefined) : undefined;
  const stats = useStatsTile(shownId, requestedSeason, requestedCompetition, category);

  // The module's own compare-against filter state — the SAME mutable object <FilterChipBar>
  // edits in place (mod._statsFilters). Seeded once; a stable reference so edits accumulate.
  const [activeFilters] = useState<ActiveFilters>(() => {
    const existing = getModState<ActiveFilters>(mod, "_statsFilters");
    if (existing) return existing;
    const fresh: ActiveFilters = {};
    setModState(mod, "_statsFilters", fresh);
    return fresh;
  });
  const [filterVersion, setFilterVersion] = useState(0);
  const bumpFilters = () => setFilterVersion((v) => v + 1);
  // A fresh clone per version — the filtered subset memo depends on `active` by reference,
  // so a new object is how an in-place chip edit reaches it (same pattern as Rankings/Filter).
  const activeSnapshot = useMemo<ActiveFilters>(() => ({ ...activeFilters }), [activeFilters, filterVersion]);

  const options = useFilterOptions();
  const population = useMemo(() => Object.values(snapshotPlayersOrNone()) as unknown as KitPlayer[], []);
  // players is a stable snapshot reference, so the reference player only changes with name.
  const refPlayer = useMemo<KitPlayer>(
    () => (players[name] ? (players[name] as unknown as KitPlayer) : {}),
    [players, name],
  );

  const categoryHasTotal = category ? categoryHasTotalMode(category) : false;
  const rawMode = getModState<string>(mod, "_statsValueMode");
  const statsValueMode = categoryHasTotal && rawMode === "total" ? "total" : "per90";
  // Live (T2-12b): the Season chip has its own state — untouched (undefined) it follows her
  // season, so it can't live in _statsFilters as a fixed value (live/stats-files.ts SeasonChip).
  const seasonChip = getModState<SeasonChip>(mod, "_statsSeasonChip");
  // Live (T2-12b): the group she's compared with, from the nightly Stats files, narrowed by the
  // chips (Ben's rules.md). Always null on the snapshot site, whose bars read the demo population.
  const group = useStatsGroup(shownId, requestedSeason, requestedCompetition, category, statsValueMode, {
    season: seasonChip,
    filters: activeSnapshot,
    leagueCleared: isLeagueCleared(mod, activeFilters, "_statsLeagueQuiet"),
  });
  // Live: the League chip (Ben, 2026-10-17) — quiet default = the dropdown's league, following it;
  // only on a league (not a cup / "All competitions"). useLeagueChip as the other modules.
  const leagueAllowed = LIVE && !!group?.chips.leagueAllowed;
  const leagueChip = useLeagueChip(
    mod,
    activeFilters,
    leagueAllowed ? group?.chips.defaultLeague : undefined,
    filterVersion,
    bumpFilters,
    "_statsLeagueQuiet",
  );

  // Live: what the chip bar shows and edits — the stored chips + the Season chip's current value
  // (a fresh object each version; onLiveChipsChange writes the edits back).
  const herSeasonId = stats ? Number(stats.seasonCode) : null;
  const liveChipView = useMemo<ActiveFilters>(() => {
    // Season first: the default chip leads the row, as in Ben's sketch (rules.md "Two rows").
    const shown = herSeasonId === null ? null : seasonChipValue(seasonChip, herSeasonId);
    // No League chip on a cup / "All competitions" (the group is that competition).
    const rest = { ...activeSnapshot };
    if (!leagueAllowed) delete rest.league;
    return shown === null ? rest : { season: shown, ...rest };
  }, [activeSnapshot, seasonChip, herSeasonId, leagueAllowed]);

  const filtered = useMemo(
    () => computeFilteredPopulation(population, activeSnapshot, refPlayer),
    [population, activeSnapshot, refPlayer],
  );

  const allMetrics = useMemo<ProtoPlayerMetric[]>(
    () => (category ? category.subcategories.flatMap((s) => s.playerMetrics) : []),
    [category],
  );
  // fullRange never changes for a (metric, scale) once loaded, so memoise the whole set on
  // the mode; the filtered extent moves with the filters, so it's keyed on the subset too.
  const fullRanges = useMemo(() => {
    const m = new Map<string, ReturnType<typeof fullRange>>();
    for (const metric of allMetrics) m.set(metric.key, fullRange(population, metric, scaleFor(metric, statsValueMode)));
    return m;
  }, [allMetrics, population, statsValueMode]);
  const filteredExtents = useMemo(() => {
    const m = new Map<string, ReturnType<typeof filteredExtent>>();
    for (const metric of allMetrics) m.set(metric.key, filteredExtent(filtered, metric, scaleFor(metric, statsValueMode)));
    return m;
  }, [allMetrics, filtered, statsValueMode]);

  useEffect(() => {
    body.style.justifyContent = stats && category ? "flex-start" : "";
  }, [body, stats, category]);

  const onSeasonChange = (next: string) => {
    setModState(mod, "_selectedSeason", next);
    setModState(mod, "_selectedSeasonPlayer", shownId);
    // null, not undefined: the undo net copies `_`-state through JSON, which drops undefined.
    setModState(mod, "_selectedCompetition", null);
    body.scrollTop = 0;
    forceRender();
  };
  const onCompetitionChange = (next: string) => {
    setModState(mod, "_selectedCompetition", next);
    setModState(mod, "_selectedCompetitionPlayer", shownId);
    body.scrollTop = 0;
    forceRender();
  };
  // Live: write the chip bar's edit back — the Season chip to its own state (removed → null;
  // changed → the season picked, which then stays put), every other chip to _statsFilters.
  const onLiveChipsChange = () => {
    const view = liveChipView;
    const shown = herSeasonId === null ? null : seasonChipValue(seasonChip, herSeasonId);
    let next: SeasonChip = seasonChip;
    if (view.season === undefined) next = null;
    else if (JSON.stringify(view.season) !== JSON.stringify(shown)) {
      next = seasonChipFromValue(view.season as string | ExcludeValue, group?.seasonOptions ?? []);
    }
    if (JSON.stringify(next) !== JSON.stringify(seasonChip)) setModState(mod, "_statsSeasonChip", next);
    for (const k of Object.keys(activeFilters)) if (view[k] === undefined) delete activeFilters[k];
    for (const k of Object.keys(view)) if (k !== "season" && view[k] !== undefined) activeFilters[k] = view[k];
    // Through the League chip's handler (it bumps): a ✕ on it empties, an edit makes it the user's.
    leagueChip.onChipsChange();
  };
  // A new chip's starting value: Season = her season; Minutes = 150+ (rules.md). The others start
  // from her (Ben's playerFilterDefault on the group's refPlayer; Age: ±3 years around her).
  const liveNewChipValue = (key: string) => {
    if (key === "season") return herSeasonId === null ? undefined : fileSeasonLabel(herSeasonId);
    if (key === "minutes") return { ...MINUTES_CHIP_DEFAULT };
    return undefined;
  };

  const onToggleMode = () => {
    setModState(mod, "_statsValueMode", statsValueMode === "total" ? "per90" : "total");
    forceRender();
  };

  // A rank-drag icon spawns a Ranking seeded with THIS metric/season/player (desktop only) —
  // via startMenuDrag (drag-to-add), exactly as renderers-stats.js:957.
  const onRankDrag = (e: ReactPointerEvent<HTMLElement>, metricKey: string, seasonCode: string) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    startMenuDrag(e.nativeEvent, "Ranking", 3, 5, {
      _rankingCategoryId: categoryId,
      _rankingMetricKey: metricKey,
      _rankingSeason: seasonCode,
      _rankingSourcePlayer: shownId,
      // Live: the tile's League chip too (Ben, 2026-10-26: Rodri's 25/26 Premier League tile gave a
      // La Liga Ranking — his league now). A copy, so the two chips stay separate; set, so the
      // Ranking keeps it instead of its own default.
      ...(LIVE && activeFilters.league !== undefined ? { _rankingFilters: { league: structuredClone(activeFilters.league) } } : {}),
    });
  };

  if (!stats || !category) {
    return <div className="stats-empty">No data.</div>;
  }

  const currentStats = stats.stats;
  const seasonCode = stats.seasonCode;
  const seasonLabel = humanizeSeasonCode(seasonCode);
  const hasCurrentStats = Object.keys(currentStats).length > 0;
  // Goalkeeping shows real data only for goalkeepers — gate the whole category rather than
  // render a page of confident zeros for an outfielder (renderers-stats.js:818).
  const isGoalkeepingGated = categoryId === "goalkeeping" && stats.positionTemplate !== "goalkeeper";
  const shortName = players[name] ? getPlayerShortName(players[name]) : pickedShortName(shownId);

  const seasonItems: StyledSelectItem[] = stats.seasonCodes.map((c) => ({ value: c, label: humanizeSeasonCode(c) }));
  const competition = stats.competition;
  const competitionShort = competition?.options.find((o) => o.value === competition.value)?.short;

  // The season select (and, live, the competition one) is always available — even in the
  // gated / no-stats states, so you can switch to one that does have data. They live in
  // main.js's existing strip: player, competition, season (Ben's order). The open list shows
  // full competition names, the button a short one, so the row stays narrow.
  const seasonSelect = (
    <SubControlsSlot mod={mod}>
      {competition && (
        <StyledSelect
          items={competition.options.map((o) => ({ value: o.value, label: o.label }))}
          value={competition.value}
          triggerLabel={competitionShort}
          onChange={onCompetitionChange}
          wrapClass="competition-select-wrap"
          extraClass="competition-select"
        />
      )}
      <StyledSelect
        items={seasonItems}
        value={seasonCode}
        onChange={onSeasonChange}
        wrapClass="season-select-wrap"
        extraClass="season-select"
      />
    </SubControlsSlot>
  );

  if (isGoalkeepingGated) {
    return (
      <>
        {seasonSelect}
        <div className="stats-wrap">
          <div className="stats-empty">
            {shortName} isn't a goalkeeper — no goalkeeping actions recorded.
          </div>
        </div>
      </>
    );
  }
  if (!hasCurrentStats) {
    return (
      <>
        {seasonSelect}
        <div className="stats-wrap">
          <div className="stats-empty">
            No stats recorded for {shortName} in {competitionShort ? `${competitionShort} ${seasonLabel}` : seasonLabel}.
          </div>
        </div>
      </>
    );
  }

  const n = filtered.length;
  const skipLabel = shouldSkipGroupLabel(category);

  // n < 1: name the active filters (the legacy uses the label, not the value, here).
  const activeKeys = Object.keys(activeSnapshot).filter((k) => activeSnapshot[k] !== undefined);
  const zeroNote =
    "No players match all of: " +
    activeKeys
      .map((k) =>
        isRangeFilter(k)
          ? `${RANGE_FILTERS[k]!.chipLabel}: ${formatRangeReadout(k, activeSnapshot[k] as RangeActive)}`
          : FILTER_LABELS[k],
      )
      .join(", ") +
    ". Remove a filter to widen the comparison.";

  // Live: the header's ? card — what this tile shows, in plain words (Ben, 2026-10-17; explain.ts).
  const seasonShown = herSeasonId === null ? undefined : seasonChipValue(seasonChip, herSeasonId);
  // Live: who a bar's "better than X%" is out of (Ben, 2026-10-18), e.g. "Premier League players compared".
  const rankLeagues = selectedValuesOf(activeSnapshot.league as CategoricalActive | undefined);
  const leaguesWho = (rankLeagues.length ? rankLeagues : group?.chips.defaultLeague ? [group.chips.defaultLeague] : []).map((l) =>
    l.replace(/\s*\([^)]*\)\s*$/, ""),
  );
  const rankWho = `${leaguesWho.length === 1 ? `${leaguesWho[0]} ` : leaguesWho.length === 2 ? `${leaguesWho.join(" and ")} ` : ""}${
    categoryId === "goalkeeping" ? "goalkeepers" : "players"
  } compared`;
  const explanation = LIVE
    ? explainStatsTile({
        playerName: shortName,
        category: category.name,
        mode: statsValueMode,
        competition: competition?.options.find((o) => o.value === competition.value)?.label ?? competitionShort ?? "",
        season: seasonLabel,
        isLeague: !!group?.chips.leagueAllowed,
        filters: activeSnapshot,
        seasonChip:
          seasonShown === undefined
            ? null
            : seasonShown === null
              ? "every season"
              : typeof seasonShown !== "string"
                ? `every season but ${seasonShown.value}`
                : seasonShown !== seasonLabel
                  ? seasonShown
                  : null,
        defaultLeague: group?.chips.defaultLeague ?? null,
        goalkeeping: categoryId === "goalkeeping",
        group: group ? (group.kind === "group" ? { kind: "group", count: group.count } : { kind: "none", label: group.label }) : null,
      })
    : [];

  return (
    <>
      {LIVE && <ModuleExplainer mod={mod} paragraphs={explanation} />}
      {seasonSelect}
      {/* Live (T2-12b): "N players match", as on the snapshot site — or, with no group, why
          (not built yet, no league that season). */}
      {LIVE ? (
        group && (
          <PopulationReadout
            mod={mod}
            body={body}
            count={group.kind === "group" ? group.count : null}
            showToggle={categoryHasTotal}
            mode={statsValueMode}
            onToggle={onToggleMode}
            label={group.kind === "group" ? `players match${group.note}` : group.label}
          />
        )
      ) : (
        <PopulationReadout
          mod={mod}
          body={body}
          count={n}
          showToggle={categoryHasTotal}
          mode={statsValueMode}
          onToggle={onToggleMode}
          label="players match"
        />
      )}
      <div className="stats-wrap">
        {/* The compare-against chips: the demo population on the snapshot site; live, the
            Stats files (T2-12b: Season, Position, Club, Nationality, Age, Minutes). */}
        {LIVE ? (
          group && (
            <div className="filter-bar">
              <FilterChipBar
                active={liveChipView}
                refPlayer={group.chips.refPlayer}
                options={{ ...group.chips.options, season: group.seasonOptions.map((o) => o.label) }}
                rangeFilters={LIVE_RANGE_FILTERS}
                onChange={onLiveChipsChange}
                addMenuKeys={group.chips.addMenu}
                labels={LIVE_CHIP_LABELS}
                quietKeys={mergeQuiet(seasonChip === undefined ? QUIET_SEASON : undefined, leagueChip.quietKeys)}
                newChipValue={liveNewChipValue}
                optionInfo={group.chips.optionInfo}
              />
            </div>
          )
        ) : (
          <div className="filter-bar">
            <FilterChipBar
              active={activeFilters}
              refPlayer={refPlayer}
              options={options}
              rangeFilters={RANGE_FILTERS}
              onChange={bumpFilters}
            />
          </div>
        )}

        {LIVE && group?.kind === "group" && group.count < 1 ? (
          <div className="stats-rows">
            <div className="empty-note">No players match these filters. Remove a filter to widen the comparison.</div>
          </div>
        ) : n < 1 && !LIVE ? (
          <div className="stats-rows">
            <div className="empty-note">{zeroNote}</div>
          </div>
        ) : (
          <div className="stats-rows">
            {category.subcategories.map((sub) => (
              <div className="stat-group" key={sub.id ?? sub.name}>
                {!skipLabel && <div className="stat-group-label">{sub.name}</div>}
                {sub.playerMetrics.map((m) => {
                  const current = metricValueForMode(currentStats, m, statsValueMode);
                  const valueText = formatValueForMode(current, m, statsValueMode);
                  const missing = current == null || Number.isNaN(current);
                  // Live: the track = her gender's whole database, the band = the group (rules.md
                  // "Reading a bar"). No group, or no track value for this row → no bar.
                  const full = LIVE ? (group?.kind === "group" ? (group.track[m.key] ?? null) : null) : fullRanges.get(m.key)!;
                  const extent = LIVE
                    ? group?.kind === "group"
                      ? (group.band[m.key] ?? null)
                      : null
                    : (filteredExtents.get(m.key) ?? null);
                  const bar = missing || !full ? null : rangeBarFraction(extent, full);
                  const markerFrac = missing || !full ? 0 : markerFraction(current as number, full);
                  return (
                    <div className="pbar-row" data-key={m.key} key={m.key}>
                      <span className="pbar-label-wrap">
                        <span className="pbar-label">{m.shortName || m.name}</span>
                        <span className="pbar-info-icon" data-tooltip={m.definition}>
                          ⓘ
                        </span>
                      </span>
                      {/* Live with no scale for this row (no group, a metric it lacks, Claim
                          Success %): no bar. `visibility` keeps the row's columns where they are. */}
                      <span className="pbar-track" style={!full ? { visibility: "hidden" } : undefined}>
                        <span
                          className="pbar-range"
                          style={
                            bar
                              ? { opacity: 1, left: `${(bar.left * 100).toFixed(1)}%`, width: `${(bar.width * 100).toFixed(1)}%` }
                              : { opacity: 0 }
                          }
                        />
                        <span
                          className="pbar-marker"
                          style={missing ? { opacity: 0 } : { opacity: 1, left: `${(markerFrac * 100).toFixed(1)}%` }}
                          {...(missing
                            ? {}
                            : {
                                "data-tooltip": (() => {
                                  // Live: where she ranks in the group (the light green band).
                                  const vs = LIVE && group?.kind === "group" ? group.values[m.key] : undefined;
                                  const pct = vs ? percentBetter(current as number, vs, isLowerBetter(m.key)) : null;
                                  const rank = pct === null || !vs ? "" : `\n${percentBetterText(pct, vs.length, rankWho, isLowerBetter(m.key))}`;
                                  return `${seasonLabel}: ${valueText}${rank}`;
                                })(),
                              })}
                        />
                      </span>
                      <span className="pbar-value">{valueText}</span>
                      <span
                        className="pbar-rank-icon"
                        data-tooltip="Drag to rank every player"
                        onPointerDown={(e) => onRankDrag(e, m.key, seasonCode)}
                      >
                        <svg viewBox="0 0 16 16" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="1.3">
                          <rect x="1.5" y="2.5" width="13" height="11" rx="1.2" />
                          <line x1="1.5" y1="6.17" x2="14.5" y2="6.17" />
                          <line x1="1.5" y1="9.83" x2="14.5" y2="9.83" />
                          <line x1="6.5" y1="2.5" x2="6.5" y2="13.5" />
                        </svg>
                      </span>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// Comparison mode: 2–5 merged players on one module, each a colour/letter, two-to-five markers
// per bar. No filters, no population readout, no rank icon, no range bar, no Per 90/Total toggle
// — comparing named players and comparing against a filtered population are different questions
// (renderers-stats.js:557). Values show only for exactly 2 players (3+ has no honest column
// layout — bars only). Seasons come from mod._mergedSeasons (each player's own season at merge
// time), colours from mod._mergedColors (index-matched, fixed for the player's lifetime).
// The snapshot site; `?live=1` is StatsComparisonLive (T2-14). Both draw ComparisonView.
function StatsComparison({ mod, body, modCfg }: ProtoModuleProps) {
  const categoryId = (modCfg as { categoryId?: string } | null)?.categoryId ?? null;
  const categories = useMetricCategories();
  const category: ProtoMetricCategory | null = categoryId ? categories.find((c) => c.id === categoryId) ?? null : null;

  const players = readSnapshot().players;
  const merged = getModState<PlayerId[]>(mod, "_mergedPlayers") ?? [];
  const mergedSeasons = getModState<string[]>(mod, "_mergedSeasons");
  const mergedColors = getModState<string[]>(mod, "_mergedColors");

  useHideSubControls(mod, body);

  const population = useMemo(() => Object.values(readSnapshot().players) as unknown as KitPlayer[], []);
  const allMetrics = useMemo<ProtoPlayerMetric[]>(
    () => (category ? category.subcategories.flatMap((s) => s.playerMetrics) : []),
    [category],
  );
  // Comparison always reads the per-90 scale (no Per 90/Total toggle) — renderers-stats.js:705.
  const fullRanges = useMemo(() => {
    const m = new Map<string, ReturnType<typeof fullRange>>();
    for (const metric of allMetrics) m.set(metric.key, fullRange(population, metric, "per90"));
    return m;
  }, [allMetrics, population]);

  const playerList = merged.map((id) => playerById(players, id));
  if (playerList.some((p) => !p) || !category) {
    return <div className="stats-empty">Comparison unavailable.</div>;
  }
  const records = playerList as RawPlayerRecord[];
  const seasonCodes = records.map((p, i) => {
    const chosen = mergedSeasons?.[i];
    return chosen && p.seasons?.[chosen] ? chosen : p.currentSeason ?? "";
  });
  const letters = (mergedColors && mergedColors.length ? mergedColors : COMPARE_LETTERS).slice(0, records.length);

  // If ANY merged player isn't a goalkeeper, the whole Goalkeeping comparison is gated
  // (renderers-stats.js:663).
  const gated = records.filter((p) => category.id === "goalkeeping" && p.positionTemplate !== "goalkeeper");

  const members = records.map<ComparisonMember>((p, i) => ({
    letter: letters[i]!,
    initialName: getPlayerInitialName(p),
    shortName: getPlayerShortName(p),
    seasonCode: seasonCodes[i]!,
    stats: p.seasons?.[seasonCodes[i]!]?.stats ?? {},
    id: playerIdOf(p),
  }));
  return (
    <ComparisonView
      mod={mod}
      category={category}
      members={members}
      gated={gated.map((p) => getPlayerShortName(p))}
      rangeOf={(key) => fullRanges.get(key)!}
    />
  );
}

/**
 * `?live=1` (T2-14): the same comparison on real numbers — each player's league and current
 * season, one dot each on a track of their leagues + seasons combined (useLiveStatsComparison). Any
 * player in the DB can be on it (a live pick too); the drop rules keep men and women apart.
 */
function StatsComparisonLive({ mod, body, modCfg }: ProtoModuleProps) {
  const categoryId = (modCfg as { categoryId?: string } | null)?.categoryId ?? null;
  const category = useStatsCategory(categoryId);
  const merged = getModState<PlayerId[]>(mod, "_mergedPlayers") ?? [];
  const mergedColors = getModState<string[]>(mod, "_mergedColors");

  useHideSubControls(mod, body);
  // Live only (Ben, 2026-10-16): merged players get the Per 90 / Total toggle too — the
  // prototype's comparison was per 90 only, an oversight. The demo site keeps it that way.
  const [, forceRender] = useReducer((n: number) => n + 1, 0);
  const hasTotal = category ? categoryHasTotalMode(category) : false;
  const mode: "per90" | "total" = hasTotal && getModState<string>(mod, "_statsValueMode") === "total" ? "total" : "per90";
  const data = useLiveStatsComparison(merged, category, mode, getModState<(string | null)[]>(mod, "_mergedSeasons") ?? []);
  if (!data || !category) {
    return <div className="stats-empty">Comparison unavailable.</div>;
  }
  const letters = (mergedColors && mergedColors.length ? mergedColors : COMPARE_LETTERS).slice(0, data.players.length);
  const members = data.players.map<ComparisonMember>((p, i) => {
    const named = namedPlayer(p.id);
    return {
      letter: letters[i]!,
      initialName: getPlayerInitialName(named),
      shortName: getPlayerShortName(named),
      seasonCode: p.seasonCode,
      stats: p.stats,
      id: p.id,
    };
  });
  const gated = category.id === "goalkeeping" ? members.filter((_, i) => data.players[i]!.positionTemplate !== "goalkeeper") : [];
  const explanation = explainStatsMerged({
    category: category.name,
    mode,
    players: members.map((m, i) => ({
      name: m.shortName,
      season: humanizeSeasonCode(m.seasonCode),
      league: data.players[i]!.league ?? null,
    })),
  });
  return (
    <>
    <ModuleExplainer mod={mod} paragraphs={explanation} />
    <ComparisonView
      mod={mod}
      category={category}
      members={members}
      gated={gated.map((m) => m.shortName)}
      rangeOf={(key) => data.track[key] ?? null}
      mode={mode}
      header={
        // The same header as a one-player tile (Ben, 2026-10-16): the count is the players on it.
        <PopulationReadout
          mod={mod}
          body={body}
          count={members.length}
          showToggle={hasTotal}
          mode={mode}
          onToggle={() => {
            setModState(mod, "_statsValueMode", mode === "total" ? "per90" : "total");
            forceRender();
          }}
          label="players match"
        />
      }
    />
    </>
  );
}

/** Who a live comparison's player is: the snapshot's record, else her live pick (her name). */
function namedPlayer(id: PlayerId): { name?: string } {
  const p = playerById(snapshotPlayersOrNone(), id) ?? pickedPlayer(id);
  if (!p) throw new Error(`Player id ${id} is neither in the snapshot nor a live pick`);
  return p;
}

// Comparison hides the whole sub-controls strip (main.js's player picker) — there's no single
// "current player" to pick across a merge (renderers-stats.js:569). Restore it on unmerge.
function useHideSubControls(mod: HTMLElement, body: HTMLElement): void {
  useEffect(() => {
    body.style.justifyContent = "flex-start";
    const strip = mod.querySelector<HTMLElement>(":scope > .module-sub-controls");
    if (!strip) return;
    const prev = strip.style.display;
    strip.style.display = "none";
    return () => {
      strip.style.display = prev;
    };
  }, [mod, body]);
}

/** One player on a comparison, as the view draws them. */
interface ComparisonMember {
  id: PlayerId;
  letter: string;
  initialName: string;
  shortName: string;
  seasonCode: string;
  stats: Record<string, number | null | undefined>;
}

/** A comparison's legend + bars, the same on both sites. `rangeOf` = a row's full scale; null
 *  (live: no track for that row) hides the bar, as on a one-player tile. */
function ComparisonView({
  mod,
  category,
  members,
  gated,
  rangeOf,
  mode = "per90",
  header = null,
}: {
  mod: HTMLElement;
  category: ProtoMetricCategory;
  members: ComparisonMember[];
  gated: string[];
  rangeOf: (metricKey: string) => Range | null;
  /** Live only: Per 90 / Total (the demo's comparison is always per 90). */
  mode?: "per90" | "total";
  /** Live only: the "N players match" row + Per 90 / Total toggle. */
  header?: ReactNode;
}) {
  const showValues = members.length === 2;
  const skipLabel = shouldSkipGroupLabel(category);

  // A legend chip is a drag source (→ Bio on empty canvas, or merges into another Stats/Radar),
  // carrying its OWN season — via startPlayerTokenDrag (drag-to-add).
  const onChipDrag = (e: ReactPointerEvent<HTMLElement>, id: PlayerId, season: string) => {
    if (window.innerWidth <= 768) return;
    e.preventDefault();
    e.stopPropagation();
    setStickyPlayer(id);
    startPlayerTokenDrag(e.nativeEvent, { _selectedSeason: season });
  };

  return (
    <div className="compare-wrap">
      {header}
      <div className="compare-legend">
        {members.map((p, i) => (
          <span
            key={i}
            className={`compare-chip compare-chip-${p.letter}`}
            style={window.innerWidth > 768 ? { cursor: "grab" } : undefined}
            onPointerDown={(e) => {
              if ((e.target as HTMLElement).closest(".compare-remove")) return;
              onChipDrag(e, p.id, p.seasonCode);
            }}
          >
            <span className="compare-dot" />
            {p.initialName}{" "}
            <span className="compare-chip-season">({humanizeSeasonCode(p.seasonCode)})</span>
            <span className="compare-remove" onClick={() => unmergePlayer(mod, i)}>
              ✕
            </span>
          </span>
        ))}
      </div>

      {gated.length ? (
        <div className="stats-empty">
          {gated.join(", ")} {gated.length > 1 ? "aren't" : "isn't"} a goalkeeper — no
          goalkeeping actions recorded.
        </div>
      ) : (
        <div className="stats-rows">
          {category.subcategories.map((sub) => (
            <div className="stat-group" key={sub.id ?? sub.name}>
              {!skipLabel && <div className="stat-group-label">{sub.name}</div>}
              {sub.playerMetrics.map((m) => {
                const full = rangeOf(m.key);
                return (
                  <div className="pbar-row" data-key={m.key} key={m.key}>
                    <span className="pbar-label-wrap">
                      <span className="pbar-label">{m.shortName || m.name}</span>
                      <span className="pbar-info-icon" data-tooltip={m.definition}>
                        ⓘ
                      </span>
                    </span>
                    {/* Live with no track for this row (not built, Claim Success %): no bar. */}
                    <span className="pbar-track" style={!full ? { visibility: "hidden" } : undefined}>
                      {members.map((p, i) => {
                        const current = metricValueForMode(p.stats, m, mode);
                        const missing = current == null || Number.isNaN(current) || !full;
                        const frac = missing || !full ? 0 : markerFraction(current as number, full);
                        return (
                          <span
                            key={i}
                            className={`pbar-marker marker-${p.letter}`}
                            style={missing ? { opacity: 0 } : { opacity: 1, left: `${(frac * 100).toFixed(1)}%` }}
                            {...(missing ? {} : { "data-tooltip": `${p.shortName}: ${formatMetricValue(current, m.outputType)}` })}
                          />
                        );
                      })}
                    </span>
                    {showValues && (
                      <span className="pbar-value pbar-value-compare">
                        {members.map((p, i) => (
                          <span key={i} className={`compare-value-${p.letter}`}>
                            {formatMetricValue(metricValueForMode(p.stats, m, mode), m.outputType)}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
